import { useEffect } from 'react'

/**
 * Overlays that belong to the question that opened them but don't live inside
 * it. The DS `Select` renders its options through a Radix portal at `<body>`,
 * so a press on one lands outside the question's own subtree even though the
 * user is still very much answering it. (The state pickers don't need this —
 * `Multiselect` and `SearchableSelect` render their dropdowns inline.)
 */
const OVERLAY_SELECTOR =
  '[data-radix-popper-content-wrapper],[role="listbox"],[role="dialog"]'

/**
 * Calls `clear` the first time the user presses anywhere that isn't the
 * question named by `activeKey` — another question, the page background, the
 * footer, anything. This is what closes up a question whose controls can't say
 * for themselves when they're finished: a slider is "answered" from its first
 * drag tick and a year/month date field from the moment a year is picked, so
 * the only reliable end-of-editing signal is the user's attention going
 * somewhere else.
 *
 * Pointer-down rather than click, so the question starts closing as the press
 * lands rather than after it's released. Capture phase, so a control that
 * stops the event on its way up still counts.
 */
export const useClearOnOutsideInteraction = (
  activeKey: string | undefined,
  clear: () => void
): void => {
  useEffect(() => {
    if (!activeKey) return undefined
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Element | null
      if (!target?.isConnected) return
      const question = document.getElementById(`question-${activeKey}`)
      if (question?.contains(target)) return
      if (target.closest(OVERLAY_SELECTOR)) return
      // While the list is non-interactive a press inside it resolves to an
      // ancestor, which would read as an outside press and close the very
      // question the guard is protecting.
      const guarded = document.querySelector('[data-motion-guard]')
      if (guarded && target.contains(guarded)) return
      clear()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true)
    // `clear` is a fresh closure each render; only the active question decides
    // whether this listener should be watching at all.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey])
}
